import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Typography,
  Box,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
const DomainApproverHeadUnitPopUp = ({
  open,
  onClose,
  selectedModels = [],
  onSave,
  headUnitData = [],
  loading = false,
  error = null,
  isRestricted = false,
  targettingData = {},
}) => {
  const [search, setSearch] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  useEffect(() => {
    setSelectedItems(
      headUnitData
        .filter((model) => {
          const modelValue = model.id || model;
          return !selectedModels.includes(modelValue);
        })
        .map((model) => model.name)
    );
  }, [selectedModels, open]);

  // Filter models based on search input
  const filteredModels = Array.isArray(headUnitData)
    ? headUnitData.filter((model) => {
        const modelName = model.name || model;
        return modelName.toLowerCase().includes(search.toLowerCase());
      })
    : [];

  const handleModelToggle = (model) => {
    const modelValue = model.name || model;

    setSelectedItems((prev) => {
      if (prev.includes(modelValue)) {
        return prev.filter((item) => item !== modelValue);
      } else {
        return [...prev, modelValue];
      }
    });
  };

  const handleSave = () => {
    const unselectedModels = filteredModels
      .filter((model) => {
        const modelValue = model.name || model;
        return !selectedItems.includes(modelValue);
      })
      .map((model) => model.id);

    onSave(unselectedModels);
    onClose();
  };

  const handleCancel = () => {
    setSelectedItems(selectedModels);
    setSearch("");
    onClose();
  };

  // Safe error message handling
  const getErrorMessage = () => {
    if (typeof error === "string") {
      return error;
    } else if (error?.message) {
      return error.message;
    } else if (error) {
      return "An error occurred while loading head unit models";
    }
    return "Unknown error";
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
          }}
        >
          <CircularProgress size={40} />
        </Box>
      );
    }
    if (error) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
            color: "#d32f2f",
            textAlign: "center",
            padding: 2,
          }}
        >
          <Typography>{getErrorMessage()}</Typography>
        </Box>
      );
    }
    if (filteredModels.length === 0) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
            color: "#666",
          }}
        >
          <Typography>
            {search
              ? "No models found matching your search"
              : "No models available"}
          </Typography>
        </Box>
      );
    }
    return filteredModels.map((model, index) => (
      <FormControlLabel
        key={model.name || model || index}
        control={
          <Checkbox
            checked={selectedItems.includes(model.name || model)}
            onChange={() => handleModelToggle(model)}
            disabled={!isRestricted}
            sx={{
              color: isRestricted ? "#1976D2" : "#BDBDBD",
              "&.Mui-checked": {
                color: isRestricted ? "#1976D2" : "#BDBDBD",
              },
            }}
          />
        }
        label={
          <Typography sx={{ fontSize: "14px", color: "#2A2C2D" }}>
            {model.name || model}
          </Typography>
        }
        sx={{
          margin: "4px 0",
          padding: "4px 8px",
          "&:hover": {
            backgroundColor: "#F5F5F5",
            borderRadius: "4px",
          },
        }}
      />
    ));
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth={false}
      slotProps={{
        paper: {
          sx: {
            width: "500px",
            height: "auto",
            borderRadius: "8px",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px 24px",
          fontSize: "18px",
          fontWeight: "600",
          color: "#2A2C2D",
        }}
      >
        Targeting - Head Unit Models
        <IconButton onClick={handleCancel} sx={{ color: "#2A2C2D" }}>
          <ClearIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: "0 24px 16px 24px" }}>
        <TextField
          fullWidth
          placeholder="Filter"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          disabled={!isRestricted}
          sx={{
            mb: 2,
            "& .MuiOutlinedInput-root": {
              borderRadius: "4px",
            },
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#9E9E9E" }} />
                </InputAdornment>
              ),
            },
          }}
        />

        <Box
          sx={{
            maxHeight: "300px",
            overflowY: "auto",
            border: "1px solid #E0E0E0",
            borderRadius: "4px",
            padding: "8px",
            minHeight: "200px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {renderContent()}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          padding: "16px 24px",
          justifyContent: isRestricted ? "space-between" : "flex-end",
          gap: 2,
        }}
      >
        <Button
          onClick={handleCancel}
          sx={{
            textTransform: "none",
            color: "#2A2C2D",
            backgroundColor: "#F5F5F5",
            border: "1px solid #E0E0E0",
            borderRadius: "24px",
            padding: "8px 24px",
            fontSize: "14px",
            fontWeight: "500",
            "&:hover": {
              backgroundColor: "#EEEEEE",
            },
          }}
        >
          {isRestricted ? "Cancel" : "Close"}
        </Button>

        {isRestricted && (
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={loading}
            sx={{
              textTransform: "none",
              backgroundColor: "#1976D2",
              borderRadius: "24px",
              padding: "8px 24px",
              fontSize: "14px",
              fontWeight: "500",
              "&:hover": {
                backgroundColor: "#1565C0",
              },
              "&:disabled": {
                backgroundColor: "#CCCCCC",
              },
            }}
          >
            Save
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

DomainApproverHeadUnitPopUp.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedModels: PropTypes.array,
  onSave: PropTypes.func.isRequired,
  headUnitData: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  isRestricted: PropTypes.bool,
  targettingData: PropTypes.object,
};

export default DomainApproverHeadUnitPopUp;
